import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, Loader } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import { supabase } from '../lib/supabase';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

const Assistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '¡Hola! Soy tu asistente virtual con IA. ¿En qué puedo ayudarte hoy?\n\nPuedo ayudarte con:\n- Consultas sobre clientes y proveedores\n- Información sobre facturas y pagos\n- Datos de producciones y muestras\n- Análisis de ventas y gastos\n- Responder preguntas sobre el sistema',
      sender: 'assistant',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [configStatus, setConfigStatus] = useState<any>(null);
  const [isCheckingConfig, setIsCheckingConfig] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Check configuration on component mount
  useEffect(() => {
    const checkConfiguration = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('openai-assistant', {
          method: 'GET'
        });
        
        if (error) {
          console.error('Configuration check failed:', error);
          setConfigStatus({ error: error.message });
        } else {
          setConfigStatus(data);
        }
      } catch (error) {
        console.error('Configuration check error:', error);
        setConfigStatus({ error: 'No se pudo verificar la configuración' });
      } finally {
        setIsCheckingConfig(false);
      }
    };

    checkConfiguration();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    try {
      // Show typing indicator
      const typingIndicatorId = Date.now() + 1;
      setMessages(prev => [...prev, {
        id: typingIndicatorId.toString(),
        text: '...',
        sender: 'assistant',
        timestamp: new Date(),
      }]);

      // Get conversation history (last 10 messages)
      const conversationHistory = messages
        .filter(msg => msg.text !== '...') // Filter out typing indicators
        .slice(-10)
        .map(msg => ({
          role: msg.sender,
          content: msg.text,
        })) || [];

      // Call the OpenAI assistant edge function
      const { data, error } = await supabase.functions.invoke('openai-assistant', {
        body: { 
          message: userMessage.text,
          history: conversationHistory
        }
      });

      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.id !== typingIndicatorId.toString()));

      if (error) {
        console.error('Edge Function Error:', error);
        throw new Error(`Error del servidor: ${error.message || 'Error desconocido'}`);
      }

      if (!data) {
        throw new Error('No se recibió respuesta del servidor');
      }

      if (data.error) {
        throw new Error(`Error del asistente: ${data.error}`);
      }

      if (!data.response) {
        throw new Error('Respuesta inválida del asistente');
      }

      const assistantMessage: Message = {
        id: (Date.now() + 2).toString(),
        text: data.response,
        sender: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error('Error calling OpenAI:', error);
      
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.id !== (Date.now() + 1).toString()));
      
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        text: `Lo siento, ocurrió un error al procesar tu solicitud: ${error.message || 'Error desconocido'}. 

Posibles causas:
- Problema de conexión con el servidor
- Error en la configuración del asistente
- Timeout en la respuesta

Por favor, intenta de nuevo más tarde o contacta al administrador si el problema persiste.`,
        sender: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Function to format message text with code blocks and SQL highlighting
  const formatMessageText = (text: string) => {
    // Split the text by code blocks
    const parts = text.split(/(```(?:sql)?\n[\s\S]*?\n```)/g);
    
    return parts.map((part, index) => {
      // Check if this part is a code block
      const codeMatch = part.match(/```(?:(sql))?\n([\s\S]*?)\n```/);
      
      if (codeMatch) {
        const language = codeMatch[1] || '';
        const code = codeMatch[2];
        
        // Render SQL with syntax highlighting
        if (language.toLowerCase() === 'sql') {
          return (
            <div key={index} className="bg-gray-800 text-white p-3 rounded-md my-2 overflow-x-auto">
              <pre className="text-sm font-mono">{code}</pre>
            </div>
          );
        }
        
        // Render other code
        return (
          <div key={index} className="bg-gray-800 text-white p-3 rounded-md my-2 overflow-x-auto">
            <pre className="text-sm font-mono">{code}</pre>
          </div>
        );
      }
      
      // Handle regular text with line breaks
      return <span key={index} className="whitespace-pre-wrap">{part}</span>;
    });
  };

  return (
    <div className="h-[calc(100vh-10rem)] flex flex-col">
      {/* Configuration Status */}
      {!isCheckingConfig && configStatus && (
        <div className="mb-4 p-3 rounded-lg border">
          <h3 className="text-sm font-semibold mb-2">Estado de la Configuración:</h3>
          {configStatus.error ? (
            <div className="text-red-600 text-sm">
              ❌ Error: {configStatus.error}
            </div>
          ) : (
            <div className="text-sm space-y-1">
              <div className={configStatus.supabaseUrl ? 'text-green-600' : 'text-red-600'}>
                {configStatus.supabaseUrl ? '✅' : '❌'} Supabase URL
              </div>
              <div className={configStatus.supabaseServiceKey ? 'text-green-600' : 'text-red-600'}>
                {configStatus.supabaseServiceKey ? '✅' : '❌'} Supabase Service Key
              </div>
              <div className={configStatus.openaiApiKey ? 'text-green-600' : 'text-red-600'}>
                {configStatus.openaiApiKey ? '✅' : '❌'} OpenAI API Key
              </div>
              {!configStatus.supabaseUrl || !configStatus.supabaseServiceKey || !configStatus.openaiApiKey ? (
                <div className="text-amber-600 text-xs mt-2">
                  ⚠️ El asistente puede no funcionar correctamente. Contacta al administrador.
                </div>
              ) : (
                <div className="text-green-600 text-xs mt-2">
                  ✅ Configuración correcta
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Card className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.sender === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.sender === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                }`}
              >
                {message.text === '...' ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 dark:bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm">{formatMessageText(message.text)}</div>
                    <p className="text-xs mt-1 opacity-70">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                className="flex-1 min-h-[2.5rem] max-h-32 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white resize-y"
                rows={1}
                disabled={isProcessing}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isProcessing}
                icon={isProcessing ? <Loader className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              >
                Enviar
              </Button>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <Bot className="h-3 w-3 mr-1" />
              Asistente con IA - Tus consultas son procesadas con OpenAI
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Assistant;